import { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useToast } from "@/components/ui/use-toast";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactSection() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Vennligst oppgi navn.";
    if (!formData.email.trim()) {
      next.email = "Vennligst oppgi e-post.";
    } else if (!EMAIL_RE.test(formData.email.trim())) {
      next.email = "Ugyldig e-postadresse.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate()) return;

    setLoading(true);
    try {
      await base44.functions.invoke("submitContactForm", {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        interest: formData.interest || "",
        address: "",
        message: formData.message.trim(),
      });
      toast({
        title: "Takk for forespørselen!",
        description: "Vi har mottatt meldingen din og svarer vanligvis innen 24 timer.",
      });
      setFormData({ name: "", email: "", phone: "", interest: "", message: "" });
      setErrors({});
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Noe gikk galt",
        description: "Prøv igjen eller kontakt oss på info@lykke.info.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 md:py-40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">
              Ta kontakt
            </p>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light leading-[1.1] mb-8">
              La oss skape
              <br />
              <span className="italic">noe vakkert sammen.</span>
            </h2>
            <div className="w-12 h-px bg-primary/40 mb-8" />
            <p className="font-body text-2xl leading-relaxed text-foreground/70 mb-10 max-w-md">
              Fortell oss om ditt prosjekt. Vi kontakter deg innen 24 timer for å diskutere mulighetene.
            </p>

          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-name" className="text-sm tracking-widest-xl font-body uppercase text-foreground/70 block mb-2">
                    Navn
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    aria-invalid={!!errors.name}
                    className="w-full bg-transparent border-b border-border/60 pb-3 font-body text-sm text-foreground focus:border-primary outline-none transition-colors"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-destructive">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-sm tracking-widest-xl font-body uppercase text-foreground/70 block mb-2">
                    E-post
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    aria-invalid={!!errors.email}
                    className="w-full bg-transparent border-b border-border/60 pb-3 font-body text-sm text-foreground focus:border-primary outline-none transition-colors"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-destructive">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-phone" className="text-sm tracking-widest-xl font-body uppercase text-foreground/70 block mb-2">
                    Telefon
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-transparent border-b border-border/60 pb-3 font-body text-sm text-foreground focus:border-primary outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-interest" className="text-sm tracking-widest-xl font-body uppercase text-foreground/70 block mb-2">
                    Interessert i
                  </label>
                  <select
                    id="contact-interest"
                    name="interest"
                    aria-label="Interessert i (velg modell)"
                    value={formData.interest}
                    onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                    className="w-full bg-transparent border-b border-border/60 pb-3 font-body text-sm text-foreground focus:border-primary outline-none transition-colors appearance-none cursor-pointer"
                  >
                    <option value="">Velg modell</option>
                    <optgroup label="LYKKE LY">
                      <option value="umi">LYKKE UMI</option>
                      <option value="hiro">LYKKE HIRO</option>
                      <option value="yori">LYKKE YORI</option>
                      <option value="tori">LYKKE TORI</option>
                      <option value="mori">LYKKE MORI</option>
                    </optgroup>
                    <optgroup label="LYKKE PANORAMA">
                      <option value="trysil">LYKKE TRYSIL</option>
                      <option value="verbier">LYKKE VERBIER</option>
                      <option value="vikka">LYKKE VIKKA</option>
                      <option value="chamonix">LYKKE CHAMONIX</option>
                    </optgroup>
                    <optgroup label="LYKKE FJELL">
                      <option value="cortina">LYKKE CORTINA</option>
                      <option value="dolomiti">LYKKE DOLOMITI</option>
                      <option value="geilo">LYKKE GEILO</option>
                      <option value="aspen">LYKKE ASPEN</option>
                    </optgroup>
                    <option value="other">Inne pytania</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="text-sm tracking-widest-xl font-body uppercase text-foreground/70 block mb-2">
                  Melding
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-transparent border-b border-border/60 pb-3 font-body text-sm text-foreground focus:border-primary outline-none transition-colors resize-none"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-3 text-sm tracking-widest-xl font-body uppercase px-8 py-4 border border-foreground/30 text-foreground hover:bg-foreground hover:text-background transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
                      Sender...
                    </>
                  ) : (
                    <>
                      Send forespørsel
                      <Send size={14} strokeWidth={1.5} />
                    </>
                  )}
                </button>
                <p className="mt-3 text-xs text-foreground/60 font-body">
                  Vi svarer vanligvis innen 24 timer.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}